import { useMemo } from "react";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useLedgers } from "@api/ledger.api";
import { useEntities } from "./useEntities";
import { useFinancialAccounts } from "./useFinancialAccounts";

interface FiltersWithEntityIds {
    entityIds?: number[];
}

interface UseBulkAccrualAccountEntityIdParams<
    TLineItem,
    TFilters extends FiltersWithEntityIds,
> {
    filters?: TFilters;
    hasSelectedByCurrentFilters: boolean;
    selectedLineItems: Record<string, TLineItem>;
    getFinancialAccountId: (
        lineItem: TLineItem,
    ) => string | number | null | undefined;
    getEntityId?: (lineItem: TLineItem) => number | null | undefined;
}

export const useBulkAccrualAccountEntityId = <
    TLineItem,
    TFilters extends FiltersWithEntityIds,
>({
    filters,
    hasSelectedByCurrentFilters,
    selectedLineItems,
    getFinancialAccountId,
    getEntityId,
}: UseBulkAccrualAccountEntityIdParams<TLineItem, TFilters>) => {
    const entities = useEntities();
    const financialAccounts = useFinancialAccounts({ onlyBusiness: true });

    const selectedAccrualAccountEntityIdCandidate = useMemo(() => {
        const filteredEntityIds = filters?.entityIds;
        if (filteredEntityIds?.length === 1) {
            return filteredEntityIds[0];
        }

        const entityIdByFinancialAccountId = Object.fromEntries(
            financialAccounts.map((financialAccount) => [
                financialAccount.id,
                financialAccount.entityId,
            ]),
        );

        if (!hasSelectedByCurrentFilters) {
            const selectedEntityIds = new Set(
                Object.values(selectedLineItems)
                    .map((lineItem) => {
                        const entityIdFromLine = getEntityId?.(lineItem);
                        if (entityIdFromLine != null) {
                            return entityIdFromLine;
                        }

                        const financialAccountId =
                            getFinancialAccountId(lineItem);
                        if (!financialAccountId) {
                            return undefined;
                        }

                        return entityIdByFinancialAccountId[financialAccountId];
                    })
                    .filter(
                        (entityId): entityId is number =>
                            entityId !== null && entityId !== undefined,
                    ),
            );

            if (selectedEntityIds.size === 1) {
                return [...selectedEntityIds][0];
            }
        }

        if (entities.length === 1) {
            return entities[0].id;
        }

        return undefined;
    }, [
        entities,
        filters,
        financialAccounts,
        getEntityId,
        getFinancialAccountId,
        hasSelectedByCurrentFilters,
        selectedLineItems,
    ]);

    const { data: selectedEntityLedgers } = useLedgers(
        selectedAccrualAccountEntityIdCandidate,
    );

    return useMemo(() => {
        const hasAccrualLedger =
            selectedEntityLedgers?.some(
                (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
            ) ?? false;

        return hasAccrualLedger
            ? selectedAccrualAccountEntityIdCandidate
            : undefined;
    }, [selectedAccrualAccountEntityIdCandidate, selectedEntityLedgers]);
};
